import {
  type Landmark,
  getLandmarksObject,
  loadKml,
} from 'src/app/shared/utils/kml';

import { Injectable } from '@angular/core';

type CacheEntry = {
  data: Record<string, Landmark>;
  timestamp: number;
};

@Injectable({
  providedIn: 'root',
})
export class KmlService {
  private cache = new Map<string, CacheEntry>();
  private readonly CACHE_DURATION_MS = 10 * 60 * 1000; // 10分

  async getLandmarks(mapID: string): Promise<Record<string, Landmark>> {
    // 期限切れキャッシュをクリーニング
    this.cleanExpiredCache();

    const cacheKey = mapID;
    const now = Date.now();

    // キャッシュ確認
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return cached.data;
    }

    // KML読み込み
    const baseUrl = 'https://ods3.illustmap.org/';
    const kmlResult = await loadKml(`${baseUrl}${mapID}.kml`);
    const landmarks = getLandmarksObject(kmlResult.mapDocument);

    // キャッシュに保存
    this.cache.set(cacheKey, {
      data: landmarks,
      timestamp: now,
    });

    return landmarks;
  }

  clearCache(mapID?: string): void {
    if (mapID) {
      this.cache.delete(mapID);
    } else {
      this.cache.clear();
    }
  }

  getCacheSize(): number {
    return this.cache.size;
  }

  private cleanExpiredCache(): void {
    const now = Date.now();
    this.cache = new Map(
      Array.from(this.cache.entries()).filter(
        ([_, entry]) => now - entry.timestamp < this.CACHE_DURATION_MS,
      ),
    );
  }

  // テスト用のキャッシュ状態確認
  isCached(mapID: string): boolean {
    const cached = this.cache.get(mapID);
    if (!cached) {
      return false;
    }

    const now = Date.now();
    return now - cached.timestamp < this.CACHE_DURATION_MS;
  }
}
